/** "New": making an item, a document or a spreadsheet (the items list's, and the triage area's into it). */

import type { ComponentChildren } from "preact";
import { useRef, useState } from "preact/hooks";
import type { ObjEntity, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import type { Action } from "../../lib/actions.ts";
import { NEW_DOCUMENT, NEW_ITEM, NEW_SPREADSHEET, useAsked } from "../../lib/asked.ts";
import { type DropTarget, upload } from "../../lib/fileDrop.ts";
import { ignore } from "../../lib/ignore.ts";
import { location } from "../../lib/router.ts";
import { Dialog } from "../../ui/Dialog.tsx";
import { EntityGlyph } from "../../ui/EntityGlyph.tsx";
import { FILE_ICONS, FolderIcon, KIND_ICONS, PlusIcon, UploadIcon } from "../../ui/icons.tsx";
import type { MenuEntry } from "../../ui/Menu.tsx";
import { NewFolderDialog } from "../folders/name.tsx";

import { type Into, NewItemForm, type What } from "./newItemForm.tsx";

const DocIcon = FILE_ICONS["revisa-doc"];
const SheetIcon = FILE_ICONS["revisa-sheet"];
const RecordIcon = KIND_ICONS.record;

/** What a "New" picks: the dialog's title for each. */
const NEW_TITLE = {
  record: "items.newRecord",
  doc: "items.newDoc",
  sheet: "items.newSheet",
} as const satisfies Record<What, string>;

/** What's being made: its kind, and the entity picked with it (a record of one, straight from the menu). */
interface Making {
  what: What;
  entity?: string;
}

/** Where "New" makes things: the space, its entities, a triage area (`triage`), the folder the list shows. */
interface Where {
  space: SpaceOut;
  entities: readonly ObjEntity[];
  triage?: Into | undefined;
  /** The folder the list shows: what's made goes into it (with its sharing), a new folder too. */
  folder?: string | null | undefined;
  /** Where files picked from the device go ("Upload files": the folder shown, or the list's top level). */
  uploadTo?: DropTarget | null | undefined;
}

/**
 * "New", as an action: a menu of what to make (an item, a document, a spreadsheet, then a record of each entity), and
 * the dialogs for the one picked (drawn wherever the action is). In a list of one kind it's that kind's (records: a
 * record, of any entity; files: documents and spreadsheets). Also when the page is asked to, by the omnibar. In a
 * triage area (`triage`), what's made goes into it.
 */
export function useCreateItem({ space, entities, triage, folder, uploadTo }: Where): {
  action: Action;
  dialogs: ComponentChildren;
} {
  const [open, setOpen] = useState<Making | null>(null);
  const [foldering, setFoldering] = useState(false);
  const picker = useRef<HTMLInputElement>(null);
  useAsked(NEW_ITEM, () => setOpen({ what: "record" }));
  useAsked(NEW_DOCUMENT, () => setOpen({ what: "doc" }));
  useAsked(NEW_SPREADSHEET, () => setOpen({ what: "sheet" }));
  const kind = triage ? null : location.value.searchParams.get("kind");
  const record = {
    key: "record",
    label: t("items.what.record"),
    icon: <RecordIcon />,
    symbol: "square.and.pencil",
    onSelect: () => setOpen({ what: "record" }),
  };
  const newFolder = {
    key: "folder",
    label: t("items.what.folder"),
    icon: <FolderIcon />,
    symbol: "folder.badge.plus",
    onSelect: () => setFoldering(true),
  };
  const files = [
    {
      key: "doc",
      label: t("items.what.doc"),
      icon: <DocIcon />,
      symbol: "doc.richtext",
      onSelect: () => setOpen({ what: "doc" }),
    },
    {
      key: "sheet",
      label: t("items.what.sheet"),
      icon: <SheetIcon />,
      symbol: "tablecells",
      onSelect: () => setOpen({ what: "sheet" }),
    },
  ];
  // In a folder: files from the device into it (as dropped ones go).
  const uploads = uploadTo
    ? [
        {
          key: "upload",
          label: t("folders.upload"),
          icon: <UploadIcon />,
          symbol: "square.and.arrow.up",
          onSelect: () => picker.current?.click(),
        },
      ]
    : [];
  // A record of each entity, under the kinds: its entity set already.
  const ofEntities: MenuEntry[] = entities.map((e, i) => ({
    key: e.id,
    label: e.name,
    icon: <EntityGlyph icon={e.icon} empty="dot" />,
    separated: i === 0,
    onSelect: () => setOpen({ what: "record", entity: e.id }),
  }));
  const action: Action =
    kind === "record" && entities.length === 0
      ? {
          key: "new",
          label: t("items.newRecord"),
          icon: <PlusIcon />,
          symbol: "plus",
          primary: true,
          fab: true,
          onSelect: () => setOpen({ what: "record" }),
        }
      : {
          key: "new",
          label: t("items.newMenu"),
          icon: <PlusIcon />,
          symbol: "plus",
          primary: true,
          fab: true,
          menu: [
            // A folder, before what goes in one (not in a triage area: nothing there is in a folder).
            ...(triage ? [] : [newFolder]),
            // Files from the device (not in a list of records: what they make are files).
            ...(kind === "record" ? [] : uploads),
            ...(kind === "file"
              ? files
              : kind === "record"
                ? [record, ...ofEntities]
                : [record, ...files, ...ofEntities]),
          ],
        };
  const dialogs = (
    <>
      <Dialog open={open !== null} onClose={() => setOpen(null)} title={open ? t(NEW_TITLE[open.what]) : ""}>
        {open ? (
          <NewItemForm
            key={`${open.what}:${open.entity ?? ""}`}
            space={space}
            entities={entities}
            what={open.what}
            entity={open.entity}
            triage={triage}
            folder={folder ?? null}
            onCancel={() => setOpen(null)}
          />
        ) : null}
      </Dialog>
      <NewFolderDialog space={space} open={foldering} parent={folder ?? null} onClose={() => setFoldering(false)} />
      {uploadTo ? (
        <input
          ref={picker}
          type="file"
          multiple={true}
          class="hidden"
          aria-label={t("folders.upload")}
          onChange={(e) => {
            const picked = [...(e.currentTarget.files ?? [])];
            e.currentTarget.value = "";
            upload(uploadTo, picked).catch(ignore);
          }}
        />
      ) : null}
    </>
  );
  return { action, dialogs };
}
